/*
 * GrowthBook reader — PDP Gallery Infographics A/B/C test.
 *
 * One experiment per PDP (values: 'control' | 'b' | 'c'):
 *   - pdp-infographics-soft-plastic   → ono-roller-soft-touch-plastic
 *   - pdp-infographics-roller-steel   → ono-roller-steel
 *   - pdp-infographics-signets        → signets-set
 *
 * URL override (persisted in sessionStorage, shared across GB tests for QA):
 *   ?gb_variant=b | c | control | off
 *
 * Emits gallery_image_viewed, gallery_infographic_viewed, pdp_time_on_page.
 */

(() => {

const HANDLE_TO_EXPERIMENT = {
  'ono-roller-soft-touch-plastic': 'pdp-infographics-soft-plastic',
  'ono-roller-steel': 'pdp-infographics-roller-steel',
  'signets-set': 'pdp-infographics-signets',
};

const OVERRIDE_STORAGE_KEY = 'gb_variant_override';

const getProductHandle = () => {
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
};

const getExperimentKey = () => {
  const handle = getProductHandle();
  return handle ? HANDLE_TO_EXPERIMENT[handle] || null : null;
};

const readOverride = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('gb_variant');
    if (fromUrl === 'off') {
      sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
      return null;
    }
    if (fromUrl !== null) {
      sessionStorage.setItem(OVERRIDE_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const persistVariantForTracking = (experimentKey, value) => {
  try {
    document.cookie = `gb_${experimentKey}_variant=${value}; path=/; max-age=2592000; SameSite=Lax`;
  } catch (e) {}
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'gb_variant_ready',
    gb_experiment: experimentKey,
    gb_variant: value,
  });
};

const applyVariantToBody = (variant) => {
  if (variant === 'b' || variant === 'c') {
    document.body.dataset.gbVariant = variant;
  } else {
    delete document.body.dataset.gbVariant;
  }
  document.querySelectorAll('media-gallery slider-component').forEach((slider) => {
    if (typeof slider.resetPages === 'function') slider.resetPages();
  });
};

window.applyPdpInfographics = (gb) => {
  const experimentKey = getExperimentKey();
  if (!experimentKey) return;

  const override = readOverride();
  if (override !== null) {
    window.ab_enabled = true;
    applyVariantToBody(override);
    persistVariantForTracking(experimentKey, override === 'b' || override === 'c' ? override : 'control');
    return;
  }

  if (!window.ab_enabled) {
    applyVariantToBody('control');
    persistVariantForTracking(experimentKey, 'holdout');
    return;
  }

  const gbVariant = window._growthbook?.getFeatureValue(experimentKey, 'control');
  applyVariantToBody(gbVariant);
  persistVariantForTracking(experimentKey, gbVariant);
};

const setupPdpInfographicsTracking = () => {
  const experimentKey = getExperimentKey();
  if (!experimentKey) return;
  const gallery = document.querySelector('media-gallery');
  if (!gallery) return;

  const getVariant = () => document.body.dataset.gbVariant || 'control';

  const slides = gallery.querySelectorAll('.product__media-item');
  if ('IntersectionObserver' in window && slides.length) {
    const seen = new WeakSet();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || seen.has(entry.target)) return;
          if (entry.target.offsetParent === null) return;
          seen.add(entry.target);
          const isInfographic = entry.target.classList.contains('product-gallery-infographic');
          const detail = {
            experiment: experimentKey,
            variant: getVariant(),
            image_type: isInfographic ? 'infographic' : 'photo',
            infographic_variant: isInfographic ? entry.target.dataset.infographicSlide : null,
            slide_id: entry.target.id,
          };
          document.dispatchEvent(new CustomEvent('gallery_image_viewed', { detail, bubbles: true }));
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'gallery_image_viewed', ...detail });
          }
          if (isInfographic) {
            document.dispatchEvent(new CustomEvent('gallery_infographic_viewed', { detail, bubbles: true }));
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'gallery_infographic_viewed', ...detail });
            }
          }
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );
    slides.forEach((slide) => io.observe(slide));
  }

  gallery.addEventListener('click', (e) => {
    const thumbButton = e.target.closest('.product-gallery-infographic[data-infographic-thumbnail] .thumbnail');
    if (!thumbButton) return;
    const thumbLi = thumbButton.closest('.product-gallery-infographic');
    const key = thumbLi.dataset.infographicThumbnail;
    const targetSlide = gallery.querySelector(`.product-gallery-infographic[data-infographic-slide="${key}"]`);
    if (!targetSlide) return;
    e.preventDefault();
    gallery.querySelectorAll('.product__media-item').forEach((el) => el.classList.remove('is-active'));
    targetSlide.classList.add('is-active');
    const scroller = targetSlide.parentElement;
    if (scroller && typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
    }
    gallery.querySelectorAll('[data-infographic-thumbnail] button').forEach((btn) =>
      btn.removeAttribute('aria-current')
    );
    thumbButton.setAttribute('aria-current', 'true');
  });

  const pageStartMs = performance.now();
  let timeReported = false;
  const reportTimeOnPage = () => {
    if (timeReported) return;
    timeReported = true;
    const detail = {
      experiment: experimentKey,
      variant: getVariant(),
      duration_ms: Math.round(performance.now() - pageStartMs),
    };
    document.dispatchEvent(new CustomEvent('pdp_time_on_page', { detail, bubbles: true }));
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'pdp_time_on_page', ...detail });
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') reportTimeOnPage();
  });
  window.addEventListener('pagehide', reportTimeOnPage);
};

const bootstrap = () => {
  setupPdpInfographicsTracking();
  // Run the reader on DOM ready too — URL override + sessionStorage apply
  // without waiting on GrowthBook's async SDK. Idempotent with the core's
  // later queue callback if GB loads afterward.
  window.applyPdpInfographics && window.applyPdpInfographics();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

})();
